/**
 * scDeck · «Сценарий или тест-кейс?» classify-carousel (S04 TD-02 · 8 JUN 2026).
 *
 * Enhances a `.sc-deck` block into a one-card-at-a-time carousel: the student
 * classifies each record (two-choice), gets instant feedback + a «signal» chip,
 * then navigates Prev/Next. Saves vertical space vs a stacked list AND lets each
 * card carry a fuller breakdown (Genie 8 JUN: «сделать одно окно, каруселью»).
 *
 * Markup contract (authored content · no-JS degradable — without this script the
 * `.sc-item`s render as a readable stacked list):
 *   <div class="sc-deck" data-answers="Сценарий|Тест-кейс">
 *     <div class="sc-deck-h">Заголовок</div>
 *     <div class="sc-deck-instr">Инструкция…</div>
 *     <div class="sc-item" data-answer="0" data-signal="только намерение">
 *       <div class="sc-q">текст записи</div>
 *       <div class="sc-fb">разбор</div>
 *     </div>
 *     … N items …
 *   </div>
 * `data-answers` = the button labels (pipe-separated · 2-5 поддерживаются, S01
 * MS-05/MS-07 используют 4-5). `data-answer` = index of the correct button. The first `.sc-q`/`.sc-fb` inside each item are
 * the question and the breakdown (innerHTML preserved). `data-signal` → chip text.
 */

interface ScItem {
  q: string;
  fb: string;
  signal: string;
  answer: number;
  picked: number | null;
}

export function initScDeck(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const decks = Array.from(root.querySelectorAll<HTMLElement>('.sc-deck'));
  for (const deck of decks) {
    if (deck.dataset.scdInit === '1') continue;
    deck.dataset.scdInit = '1';
    // Saved so cleanup can restore authored content — React 18 StrictMode (dev)
    // double-invokes effects (mount → cleanup → mount); without restore the second
    // init finds the rebuilt shell (no `.sc-item`s), bails, and the carousel is left
    // with its nav listeners already removed → «Далее» dead. Restoring lets re-init rewire.
    const original = deck.innerHTML;

    const labels = (deck.dataset.answers ?? 'Вариант A|Вариант B').split('|');
    const heading = deck.querySelector('.sc-deck-h')?.textContent ?? '';
    const instr = deck.querySelector('.sc-deck-instr')?.innerHTML ?? '';
    // Final-screen takeaway · authored per deck (data-fin). Fallback = the original
    // TD-02 scenario/test-case message so existing decks without data-fin are unchanged.
    const finMsg =
      deck.dataset.fin ??
      'Сигнал простой: <strong>есть шаги и данные → тест-кейс</strong>, <strong>только намерение «что проверяем» → сценарий</strong>.';

    const items: ScItem[] = Array.from(deck.querySelectorAll<HTMLElement>('.sc-item')).map(
      (el) => ({
        q: el.querySelector('.sc-q')?.innerHTML ?? '',
        fb: el.querySelector('.sc-fb')?.innerHTML ?? '',
        signal: el.dataset.signal ?? '',
        answer: Number(el.dataset.answer ?? 0),
        picked: null,
      }),
    );
    if (items.length === 0) continue;

    let idx = 0;

    // Build the enhanced shell (replaces the stacked authored content).
    deck.innerHTML = '';
    deck.classList.add('scd-ready');

    const head = document.createElement('div');
    head.className = 'sc-head';
    const ttl = document.createElement('span');
    ttl.className = 'ttl';
    ttl.textContent = heading;
    const dots = document.createElement('span');
    dots.className = 'sc-dots';
    items.forEach(() => dots.appendChild(document.createElement('i')));
    head.append(ttl, dots);

    const stage = document.createElement('div');
    stage.className = 'sc-stage';
    const bodyEl = document.createElement('div');
    bodyEl.className = 'sc-body';
    const foot = document.createElement('div');
    foot.className = 'sc-foot';
    const prevB = document.createElement('button');
    prevB.type = 'button';
    prevB.className = 'sc-nav';
    prevB.textContent = '← Назад';
    const scoreEl = document.createElement('span');
    scoreEl.className = 'sc-score';
    const nextB = document.createElement('button');
    nextB.type = 'button';
    nextB.className = 'sc-nav go';
    foot.append(prevB, scoreEl, nextB);
    stage.append(bodyEl, foot);

    const fin = document.createElement('div');
    fin.className = 'sc-fin';

    if (instr) {
      const intro = document.createElement('div');
      intro.className = 'sc-intro';
      intro.innerHTML = instr;
      deck.append(head, intro, stage, fin);
    } else {
      deck.append(head, stage, fin);
    }

    const score = () => items.filter((it) => it.picked === it.answer).length;

    const paintDots = () => {
      Array.from(dots.children).forEach((d, k) => {
        d.className = '';
        if (k === idx) d.classList.add('cur');
        const it = items[k]!;
        if (it.picked !== null) d.classList.add(it.picked === it.answer ? 'ok' : 'no');
      });
    };

    const updNav = () => {
      prevB.disabled = idx === 0;
      const answered = items[idx]!.picked !== null;
      nextB.disabled = !answered;
      nextB.textContent = idx === items.length - 1 ? 'Завершить' : 'Далее →';
      scoreEl.innerHTML = `верно <b>${score()}</b> / ${items.length}`;
    };

    const render = () => {
      const it = items[idx]!;
      const parts: string[] = [];
      parts.push(`<div class="sc-pos">запись ${idx + 1} из ${items.length}</div>`);
      parts.push(`<div class="sc-q">${it.q}</div>`);
      parts.push(
        `<div class="sc-btns">${labels
          .map((l, j) => `<button type="button" class="sc-btn" data-p="${j}">${l}</button>`)
          .join('')}</div>`,
      );
      if (it.picked !== null) {
        const ok = it.picked === it.answer;
        parts.push(
          `<div class="sc-fbwrap"><div class="sc-verdict ${ok ? 'ok' : 'no'}">${ok ? 'Верно' : 'Не совсем'}</div>` +
            (it.signal ? `<span class="sc-signal">сигнал · ${it.signal}</span>` : '') +
            `<div class="sc-fbtext">${it.fb}</div></div>`,
        );
      }
      bodyEl.innerHTML = parts.join('');

      const btns = Array.from(bodyEl.querySelectorAll<HTMLButtonElement>('.sc-btn'));
      if (it.picked !== null) {
        btns.forEach((b) => {
          b.classList.add('lock');
          const p = Number(b.dataset.p);
          if (p === it.picked) b.classList.add(it.picked === it.answer ? 'right' : 'wrong');
          if (it.picked !== it.answer && p === it.answer) b.classList.add('correctmark');
        });
      } else {
        btns.forEach((b) => {
          const onPick = () => {
            items[idx]!.picked = Number(b.dataset.p);
            render();
          };
          b.addEventListener('click', onPick);
        });
      }
      paintDots();
      updNav();
    };

    const onPrev = () => {
      if (idx > 0) {
        idx--;
        render();
      }
    };
    const onNext = () => {
      if (idx < items.length - 1) {
        idx++;
        render();
      } else {
        stage.style.display = 'none';
        fin.style.display = 'block';
        fin.innerHTML =
          `<div class="sc-fin-big">Готово · ${score()} из ${items.length}</div>` +
          `<div class="sc-fin-sub">${finMsg}</div>`;
      }
    };
    prevB.addEventListener('click', onPrev);
    nextB.addEventListener('click', onNext);
    cleanups.push(() => {
      prevB.removeEventListener('click', onPrev);
      nextB.removeEventListener('click', onNext);
      deck.innerHTML = original;
      delete deck.dataset.scdInit;
    });

    render();
  }

  return () => cleanups.forEach((fn) => fn());
}
